'use client';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { BaseImage } from '@/components/baseImage/BaseImage';
import { Button } from '@/components/button/Button';
import { Link } from '@/components/link/Link';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import { VariantComponentProps } from '../types';

/**
 * Scroll depth, in pixels, at which the floating CTA flies in (WB-784).
 * Lives here rather than in `../const` because that module imports this one,
 * and importing it back would close an import cycle.
 */
const SCROLL_THRESHOLD_PX = 300;

const FloatingCTA: React.FC<VariantComponentProps<'floating'>> = ({
  description,
  title,
  image,
  variant,
  link,
  theme = 'light',
}) => {
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const [isMounted, setIsMounted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  // Latches once the CTA has flown in, so it never re-triggers. Deliberately a
  // ref and not `isVisible`: `handleClose` sets `isVisible` false for 500ms to
  // animate out, which would otherwise re-arm the listener and re-show it.
  const hasTriggered = useRef(false);
  const isDark = theme === 'dark';

  useEffect(() => {
    setPortalRoot(document?.body);

    const checkScroll = () => {
      if (hasTriggered.current) return;
      if (window.scrollY < SCROLL_THRESHOLD_PX) return;

      hasTriggered.current = true;
      setIsVisible(true);
      window.removeEventListener('scroll', handleScroll);
    };

    const handleScroll = () => window.requestAnimationFrame(checkScroll);

    // Browsers restore scroll position on reload and back-navigation, so a
    // visitor can land already past the threshold without ever firing a
    // scroll event.
    checkScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    window.setTimeout(() => {
      setIsMounted(false);
    }, 500);
  };

  if (!portalRoot || !isMounted) return null;

  return ReactDOM.createPortal(
    <div
      id={`react-portal-${variant}`}
      className={clsx(
        'fixed bottom-6 right-4 z-40 ml-4 w-auto max-w-[520px] overflow-hidden rounded-xl border shadow-sm transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none md:right-8 md:w-[520px]',
        'fixed bottom-6 right-4 z-40 ml-4 w-auto max-w-[520px] overflow-hidden rounded-xl border shadow-sm transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none md:right-8 md:w-[520px]',
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0',
        isDark
          ? 'border-neutral-100 bg-brand-700'
          : 'border-neutral-100 bg-white',
      )}
    >
      <Button
        aria-label="Close floating call to action"
        leftIcon={{ name: 'x' }}
        variant="tertiary"
        sizes="small"
        className={clsx(
          'absolute right-2.5 top-2.5 z-10 !inline-flex !h-5 !w-5 items-center justify-center !rounded-none !p-0',
          isDark
            ? '!text-white/90 hover:!text-white'
            : '!text-brand-700 hover:!text-brand-700',
        )}
        onClick={handleClose}
      />

      <div
        className={clsx(
          'flex flex-col gap-4 px-5 py-8 md:grid md:grid-cols-[minmax(0,1fr)_198px] md:items-center md:gap-4 md:px-7 md:py-14',
        )}
      >
        {image && (
          <div
            className={clsx(
              'relative order-1 h-[202px] w-full overflow-hidden md:order-2 md:h-full md:min-h-[202px] md:w-[197px]',
              'bg-gradient-to-r from-brand-300 to-brand-100',
            )}
          >
            <BaseImage
              {...image}
              overrideWidth="100%"
              overrideHeight="100%"
              className="absolute inset-0 h-full w-full overflow-hidden bg-white [&>picture]:block [&>picture]:h-full [&>picture]:w-full"
              classNameImage={clsx(
                'block h-full w-full object-cover',
                isDark ? 'opacity-95' : 'opacity-100',
              )}
              prerender={true}
              quality={50}
              sizes="(max-width: 768px) 520px, 197px"
            />
          </div>
        )}

        <div className="order-2 flex h-full min-w-0 flex-col items-start md:order-1">
          {title && (
            <h2
              className={clsx(
                'm-0 w-full text-xl font-medium leading-[1.15] tracking-[-0.2px] md:max-w-[270px]',
                isDark ? 'text-white' : 'text-black',
              )}
            >
              {title}
            </h2>
          )}
          {description?.content && (
            <SerializeDescription
              description={description.content}
              className={clsx(
                'mt-4 w-full md:max-w-[269px]',
                isDark
                  ? 'content-dark text-white'
                  : 'headline-content text-neutral-600',
              )}
            />
          )}
          {link?.href && (
            <Link
              {...link}
              variant={link.variant ?? 'secondary'}
              sizes={link.sizes ?? 'medium'}
              className={clsx(
                'mt-4 inline-flex min-h-[44px] w-full items-center justify-center !rounded-3xl !px-[18px] !py-[10px] text-base font-normal leading-[22px] tracking-[-0.08px] transition-colors md:w-auto',
                isDark
                  ? '!border-neutral-100 !bg-white !text-neutral-800 hover:!bg-neutral-25'
                  : '!border-neutral-100 !bg-white !text-neutral-800 hover:!bg-neutral-25',
              )}
            >
              {link.title}
            </Link>
          )}
        </div>
      </div>
    </div>,
    portalRoot,
  );
};

export default FloatingCTA;
